import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorResponseBody } from '../models';

const HTML_LIKE = /^\s*<|^\s*<!doctype|^\s*\[object/i;

const DEVELOPER_TYPE = /^\s*[A-Z]\w*(\.[A-Z]\w*)+\b/;

const DEVELOPER_DETAIL =
  /\bstack\s*trace\b|\bstackTrace\b|\bHResult\b|\bat\s+[\w.$]+\(|\bException\b/i;

const MAX_USER_MESSAGE_LENGTH = 240;

/** Rejects blank, HTML/error-page and other non-user-facing payloads. */
function isUsableMessage(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_USER_MESSAGE_LENGTH) return false;
  if (HTML_LIKE.test(trimmed)) return false;
  return !DEVELOPER_TYPE.test(trimmed) && !DEVELOPER_DETAIL.test(trimmed);
}

export function shouldSuppressErrorBody(status: number): boolean {
  return status === 403 || status >= 500;
}

export function isStructuredApiError(
  error: HttpErrorResponse,
): error is HttpErrorResponse & { error: HttpErrorResponseBody } {
  return typeof error.error === 'object' && error.error !== null;
}

/**
 * Pulls every user-facing message out of an error body.
 *
 * The gateway/proxy in front of the file service returns 400s whose body does
 * **not** match the documented envelope: `error` can be a bare message string,
 * a `ValidationProblemDetails` payload puts messages under `errors`/`title`, and
 * some layers answer with `text/plain` or an HTML error page. All of those
 * shapes used to produce zero toasts.
 */
export function extractErrorMessages(
  errorBody: HttpErrorResponseBody | string | null | undefined,
): string[] {
  const messages: string[] = [];

  const push = (value: unknown): void => {
    if (isUsableMessage(value)) {
      messages.push(value.trim());
    }
  };

  if (typeof errorBody === 'string') {
    push(errorBody);
    return messages;
  }

  if (!errorBody) {
    return messages;
  }

  // `error` is an object on the documented envelope, a bare string on 4xx.
  if (typeof errorBody.error === 'string') {
    push(errorBody.error);
  } else {
    errorBody.error?.details?.forEach((detail) => push(detail?.message));
  }

  errorBody.messages?.forEach((message) => push(message?.description));

  errorBody.details?.forEach((detail) => push(detail?.message));

  errorBody.exception?.messages?.forEach((message) =>
    push(message?.description),
  );

  // ValidationProblemDetails / ProblemDetails.
  if (errorBody.errors) {
    Object.values(errorBody.errors).forEach((entry) => {
      if (Array.isArray(entry)) {
        entry.forEach(push);
      } else {
        push(entry);
      }
    });
  }

  if (messages.length > 0) {
    return [...new Set(messages)];
  }

  // Generic wrappers only when nothing specific was found — otherwise a
  // boilerplate English `title`/`message` stacks on top of the real message.
  push(errorBody.message);
  push(errorBody.title);

  return [...new Set(messages)];
}

export function getUserErrorMessage(error: HttpErrorResponse): string {
  if (shouldSuppressErrorBody(error.status)) {
    return getFallbackErrorMessage(error.status);
  }

  return (
    extractErrorMessages(error.error)[0] ??
    getFallbackErrorMessage(error.status)
  );
}

/**
 * Last-resort message for failures that carry no recognisable payload at all —
 * e.g. a `status === 0` network error. Without this the interceptor rethrows
 * silently and the user sees nothing happen.
 */
export function getFallbackErrorMessage(status: number): string {
  if (status === 0) {
    return 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.';
  }

  if (status === 403) {
    return 'دسترسی به این مورد برای حساب شما مجاز نیست.';
  }

  if (status === 404) {
    return 'موردی یافت نشد.';
  }

  if (status === 429) {
    return 'تعداد تلاش‌ها بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.';
  }

  if (status >= 500) {
    return 'خطایی در سرور رخ داد. لطفاً کمی بعد دوباره تلاش کنید.';
  }

  return 'خطایی رخ داد. لطفاً دوباره تلاش کنید.';
}
